import axios from 'axios';
import Config from 'react-native-config';
import store from 'store/ReduxStoreContainer';
import { apiKeySelector } from 'store/appSettings/selectors';
import {
  DistanceMartixResult,
  RouteResult,
  GeoCodeLocation,
  PlacePrediction,
  GeoCodeOptions
} from './types';

const locationBase = Config.LOCATION_BASE_URL;
const geocodeUrl = '/google/geocode';
const directionsUrl = '/google/directions';
const matrixUrl = '/google/distance/matrix';
const placePredictionsUrl = '/google/place/predictions';

/*
 * Method: getDistanceMatrix
 * Description: Returns a DistanceMatrix from an origin and destinations.
 * Accepts:
 *   origin: string (can be an addresss string or a lat lng string. example: '20.776,-56.2883')
 *   destinations: string[] (can be an addresss string or a lat lng string. example: '20.776,-56.2883')
 * Docs: https://developers.google.com/maps/documentation/distance-matrix/intro
 */
export function getDistanceMatrix(
  origin: string,
  destinations: string[]
): Promise<DistanceMartixResult> {
  const originEncoded = encodeURIComponent(origin);
  const destinationsEncoded = destinations
    .map(destination => encodeURIComponent(destination))
    .join('|');
  const url = `${locationBase}${matrixUrl}?units=imperial&origins=${originEncoded}&destinations=${destinationsEncoded}`;
  const headers = { apiKey: apiKeySelector(store.getState()) };
  return axios(url, {
    headers
  }).then((res: { data: DistanceMartixResult }) => res.data);
}

/*
 * Method: getDirections
 * Description: Returns a RouteResult from an origin and destination.
 * Accepts:
 *   origin: string (can be an addresss string or a lat lng string. example: '20.776,-56.2883')
 *   destination: string (can be an addresss string or a lat lng string. example: '20.776,-56.2883')
 * Docs: https://developers.google.com/maps/documentation/directions/start
 */
export function getDirections(origin: string, destination: string): Promise<RouteResult> {
  const originEncoded = encodeURIComponent(origin);
  const destinationEncoded = encodeURIComponent(destination);
  const url = `${locationBase}${directionsUrl}?origin=${originEncoded}&destination=${destinationEncoded}`;
  const headers = { apiKey: apiKeySelector(store.getState()) };
  return axios(url, {
    headers
  }).then((res: { data: RouteResult }) => res.data);
}

/*
 * Method: geoCodeLocation
 * Description: Returns a GeoCodeLocation[] from an address
 * Accepts:
 *   address: string (can be an addresss string or a lat lng string. example: '20.776,-56.2883')
 * Docs: https://developers.google.com/maps/documentation/geocoding/intro
 */
export function geoCodeLocation(
  address: string,
  options?: GeoCodeOptions
): Promise<GeoCodeLocation[]> {
  const encoded = encodeURIComponent(address);
  let url = `${locationBase}${geocodeUrl}?address=${encoded}`;
  const headers = { apiKey: apiKeySelector(store.getState()) };
  if (options) {
    Object.keys(options).forEach(key => {
      url += `&${key}=${options[key as keyof GeoCodeOptions]}`;
    });
  }
  return axios(url, {
    headers
  }).then((res: { data: GeoCodeLocation[] }) => res.data);
}

/*
 * Method: getPlacePredictions
 * Description: Returns a GeoCodeLocation[] from an address
 * Accepts:
 *   address: string (can be an addresss string or a lat lng string. example: '20.776,-56.2883')
 * Docs: https://developers.google.com/maps/documentation/geocoding/intro
 */
export function getPlacePredictions(address: string): Promise<PlacePrediction[]> {
  const encoded = encodeURIComponent(address);
  const headers = { apiKey: apiKeySelector(store.getState()) };
  const url = `${locationBase}${placePredictionsUrl}?address=${encoded}`;

  return axios(url, {
    headers
  }).then((res: { data: PlacePrediction[] }) => res.data);
}
